// src/sessions.ts
import * as jose from "jose";
import { accessTokenPayloadSchema } from "./schema-fields.js";
import { StackAssertionError, throwErr } from "./utils/errors.js";
import { runAsynchronously, wait } from "./utils/promises.js";
import { Store } from "./utils/stores.js";
function decodeAccessTokenIfValid(token) {
  try {
    const payload = jose.decodeJwt(token);
    return accessTokenPayloadSchema.validateSync(payload);
  } catch (e) {
    return null;
  }
}
var AccessToken = class _AccessToken {
  constructor(token) {
    this.token = token;
    if (token === "undefined") {
      throw new StackAssertionError("Access token is the string 'undefined'; it's unlikely this is the correct value. They're supposed to be unguessable!");
    }
  }
  static createIfValid(token) {
    const payload = decodeAccessTokenIfValid(token);
    if (!payload) return null;
    return new _AccessToken(token);
  }
  get payload() {
    return decodeAccessTokenIfValid(this.token) ?? throwErr("Invalid access token in payload (should've been validated in createIfValid)", { token: this.token });
  }
  get expiresAt() {
    const { exp } = this.payload;
    if (exp === void 0) return /* @__PURE__ */ new Date(864e13);
    return new Date(exp * 1e3);
  }
  get issuedAt() {
    const { iat } = this.payload;
    return new Date(iat * 1e3);
  }
  /**
   * @returns The number of milliseconds until the access token expires, or 0 if it has already expired.
   */
  get expiresInMillis() {
    return Math.max(0, this.expiresAt.getTime() - Date.now());
  }
  get issuedMillisAgo() {
    return Math.max(0, Date.now() - this.issuedAt.getTime());
  }
  isExpired() {
    return this.expiresInMillis <= 0;
  }
};
var RefreshToken = class {
  constructor(token) {
    this.token = token;
    if (token === "undefined") {
      throw new StackAssertionError("Refresh token is the string 'undefined'; it's unlikely this is the correct value. They're supposed to be unguessable!");
    }
  }
};
var InternalSession = class _InternalSession {
  constructor(_options) {
    this._options = _options;
    /**
     * Whether the session as a whole is known to be invalid (ie. both access and refresh tokens are invalid). Used as a cache to avoid making multiple requests to the server (sessions never go back to being valid after being invalidated).
     *
     * It is possible for the access token to be invalid but the refresh token to be valid, in which case the session is
     * still valid (just needs a refresh). It is also possible for the access token to be valid but the refresh token to
     * be invalid, in which case the session is also valid (eg. if the refresh token is null because the user only passed
     * in an access token, eg. in a server-side request handler).
     */
    this._knownToBeInvalid = new Store(false);
    this._refreshPromise = null;
    this._accessToken = new Store(_options.accessToken ? AccessToken.createIfValid(_options.accessToken) : null);
    this._refreshToken = _options.refreshToken ? new RefreshToken(_options.refreshToken) : null;
    if (_options.accessToken === null && _options.refreshToken === null) {
      this._knownToBeInvalid.set(true);
    }
    this.sessionKey = _InternalSession.calculateSessionKey({ accessToken: _options.accessToken ?? null, refreshToken: _options.refreshToken });
  }
  static calculateSessionKey(ofTokens) {
    if (ofTokens.refreshToken) {
      return `refresh-${ofTokens.refreshToken}`;
    } else if (ofTokens.accessToken) {
      return `access-${ofTokens.accessToken}`;
    } else {
      return "not-logged-in";
    }
  }
  isKnownToBeInvalid() {
    return this._knownToBeInvalid.get();
  }
  /**
   * Marks the session object as invalid, meaning that the refresh and access tokens can no longer be used. There is no
   * way out of this state, and the session object will never return valid tokens again.
   */
  markInvalid() {
    this._accessToken.set(null);
    this._knownToBeInvalid.set(true);
  }
  onInvalidate(callback) {
    return this._knownToBeInvalid.onChange(() => callback());
  }
  getRefreshToken() {
    if (this.isKnownToBeInvalid()) return null;
    return this._refreshToken;
  }
  /**
   * Returns the access token if it is found in the cache and not expired yet, or null otherwise. Never fetches new tokens.
   */
  getAccessTokenIfNotExpiredYet(minMillisUntilExpiration, maxMillisSinceIssued) {
    if (minMillisUntilExpiration > 45e3) {
      throw new Error(`Required access token expiry ${minMillisUntilExpiration}ms is too long; access tokens are too short to be used for more than 45s`);
    }
    if (maxMillisSinceIssued !== null && maxMillisSinceIssued < 15e3) {
      throw new Error(`Required access token issuance ${maxMillisSinceIssued}ms is too short; assume that access token generation can take at least 15s`);
    }
    const accessToken = this._getPotentiallyInvalidAccessTokenIfAvailable();
    if (!accessToken || accessToken.expiresInMillis < minMillisUntilExpiration) return null;
    if (maxMillisSinceIssued !== null && accessToken.issuedMillisAgo > maxMillisSinceIssued) return null;
    return accessToken;
  }
  /**
   * Returns the access token if it is found in the cache, fetching it otherwise.
   *
   * This is usually the function you want to call to get an access token. Either set `minMillisUntilExpiration` to a reasonable value, or catch errors that occur if it expires, and call `markAccessTokenExpired` to mark the token as expired if so (after which a call to this function will always refetch the token).
   *
   * @returns null if the session is known to be invalid, cached tokens if they exist in the cache and the access token hasn't expired yet (the refresh token might still be invalid), or new tokens otherwise.
   */
  async getOrFetchLikelyValidTokens(minMillisUntilExpiration, maxMillisSinceIssued) {
    if (this.isKnownToBeInvalid()) return null;
    const accessToken = this.getAccessTokenIfNotExpiredYet(minMillisUntilExpiration, maxMillisSinceIssued);
    if (!accessToken) {
      const newTokens = await this.fetchNewTokens();
      const expiresInMillis = newTokens?.accessToken.expiresInMillis;
      const issuedMillisAgo = newTokens?.accessToken.issuedMillisAgo;
      if (expiresInMillis !== void 0 && expiresInMillis < minMillisUntilExpiration) {
        throw new StackAssertionError(`Required access token expiry ${minMillisUntilExpiration}ms is too long; access tokens are too short when they're generated (${expiresInMillis}ms)`);
      }
      if (maxMillisSinceIssued !== null && issuedMillisAgo !== void 0 && issuedMillisAgo > maxMillisSinceIssued) {
        throw new StackAssertionError(`Required access token issuance ${maxMillisSinceIssued}ms is too short; access token issuance is too slow (${issuedMillisAgo}ms)`);
      }
      return newTokens;
    }
    return { accessToken, refreshToken: this.getRefreshToken() };
  }
  /**
   * Fetches new tokens that are, at the time of fetching, guaranteed to be valid.
   *
   * The newly generated tokens are short-lived, so it's good practice not to rely on their validity (if possible). However, this function is useful in some cases where you only want to pass access tokens to a service, and you want to make sure said access token has the longest possible lifetime.
   *
   * In most cases, you should prefer `getOrFetchLikelyValidTokens`.
   *
   * @returns null if the session is known to be invalid, or new tokens otherwise (which, at the time of fetching, are guaranteed to be valid).
   */
  async fetchNewTokens() {
    const accessToken = await this._getNewlyFetchedAccessToken();
    return accessToken ? { accessToken, refreshToken: this._refreshToken } : null;
  }
  /**
   * Manually mark the access token as expired, even if the date on its payload may still be valid.
   *
   * You don't usually have to call this function anymore, but you may want to call suggestAccessTokenExpired
   * to hint that the access token should be refreshed as its data may have changed, if possible.
   */
  markAccessTokenExpired(accessToken) {
    if (!accessToken || this._accessToken.get()?.token === accessToken.token) {
      this._accessToken.set(null);
    }
  }
  /**
   * Strongly suggests that the access token should be refreshed as its data may have changed, although it's up to this
   * implementation to decide whether or when the access token will be refreshed.
   *
   * This is particularly useful when the data associated with the access token may have changed for example due to an
   * update to the user's profile.
   *
   * The current implementation marks the access token as expired if and only if a refresh token is available (regardless of
   * whether the refresh token is actually valid or not), although this is not a guarantee and subject to change.
   *
   * If you need a stronger guarantee of revoking an access token, use markAccessTokenExpired instead.
   */
  suggestAccessTokenExpired() {
    if (this._refreshToken) {
      this.markAccessTokenExpired();
    }
  }
  startRefreshingAccessToken(minMillisUntilExpiration, maxMillisSinceIssued) {
    let canceled = false;
    runAsynchronously(async () => {
      while (!canceled) {
        const tokens = await this.getOrFetchLikelyValidTokens(minMillisUntilExpiration, maxMillisSinceIssued);
        if (!tokens) return;
        const nextRefreshIn = Math.min(
          tokens.accessToken.expiresInMillis - minMillisUntilExpiration,
          (maxMillisSinceIssued ?? Infinity) - tokens.accessToken.issuedMillisAgo
        );
        await wait(Math.max(1, nextRefreshIn));
      }
    });
    return {
      unsubscribe: () => {
        canceled = true;
      }
    };
  }
  /**
   * Note that a callback invocation with `null` does not mean the session has been invalidated; the access token may just have expired. Use `onInvalidate` to detect invalidation.
   */
  onAccessTokenChange(callback) {
    return this._accessToken.onChange(callback);
  }
  /**
   * @returns An access token, which may be expired or expire soon, or null if it is known to be invalid.
   */
  _getPotentiallyInvalidAccessTokenIfAvailable() {
    if (!this._refreshToken) return null;
    if (this.isKnownToBeInvalid()) return null;
    const accessToken = this._accessToken.get();
    if (accessToken && !accessToken.isExpired()) return accessToken;
    return null;
  }
  /**
   * You should prefer `_getOrFetchPotentiallyInvalidAccessToken` in almost all cases.
   *
   * @returns A newly fetched access token (never read from cache), or null if the session either does not represent a user or the session is invalid.
   */
  async _getNewlyFetchedAccessToken() {
    if (!this._refreshToken) return null;
    if (this._knownToBeInvalid.get()) return null;
    if (!this._refreshPromise) {
      this._refreshAndSetRefreshPromise(this._refreshToken);
    }
    return await this._refreshPromise;
  }
  _refreshAndSetRefreshPromise(refreshToken) {
    let refreshPromise = this._options.refreshAccessTokenCallback(refreshToken).then((accessToken) => {
      if (refreshPromise === this._refreshPromise) {
        this._refreshPromise = null;
        this._accessToken.set(accessToken);
        if (!accessToken) {
          this.markInvalid();
        }
      }
      return accessToken;
    });
    this._refreshPromise = refreshPromise;
  }
};
export {
  AccessToken,
  InternalSession,
  RefreshToken
};
//# sourceMappingURL=sessions.js.map